import { useState, type ReactNode } from "react";
import { MapPin, Pencil } from "lucide-react";
import { LoadingButton } from "@/components/loading-button";
import { ReviewChoiceGroup } from "@/components/review-choice-group";
import { Button } from "@/components/ui/button";
import { InlineFieldInput } from "@/components/inline-field-group";
import { cn } from "@/lib/utils";
import type { DeliveryAddress, Shipment, ShipTo } from "./shipping-api";

const ADDRESS_FIELDS = [
  ["name", "Delivery site", "sm:col-span-3"],
  ["line1", "Street", "sm:col-span-3"],
  ["line2", "Suite / unit", "sm:col-span-2"],
  ["city", "City", "sm:col-span-2"],
  ["state", "State", "sm:col-span-1"],
  ["postal_code", "ZIP", "sm:col-span-1"],
] as const;
const CONTACT_FIELDS = [
  ["name", "Contact", "text"],
  ["email", "Email", "email"],
  ["phone", "Phone", "tel"],
] as const;
const EMPTY: Required<ShipTo> = {
  address: {
    name: "",
    line1: "",
    line2: "",
    city: "",
    state: "",
    postal_code: "",
    country: "US",
  },
  contact: { name: "", email: "", phone: "" },
};

/** "Site · Street, City, ST ZIP" for the one-line summary. */
function addressLine(address: Partial<DeliveryAddress>) {
  const place = [
    [address.city, address.state].filter(Boolean).join(", "),
    address.postal_code,
  ]
    .filter(Boolean)
    .join(" ");
  return [address.name, address.line1, place].filter(Boolean).join(" · ");
}

function complete(address: DeliveryAddress) {
  return [
    address.line1,
    address.city,
    address.state,
    address.postal_code,
  ].every((value) => value.trim());
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="grid gap-1 text-xs font-medium text-muted-foreground">
      {label}
      {children}
    </label>
  );
}

/**
 * Where one manufacturer's PO ships: the quote's delivery address, or its own (USA-310).
 * Freight for the shipment is priced to it and the PO prints it.
 */
export function ShipToRow({
  shipment,
  editing,
  busy,
  pending,
  onEdit,
  onSave,
}: {
  shipment: Shipment;
  editing: boolean;
  busy: boolean;
  pending: boolean;
  onEdit: (open: boolean) => void;
  onSave: (shipTo: ShipTo | null) => void;
}) {
  const saved = shipment.ship_to ?? null;
  const [own, setOwn] = useState(saved !== null);
  const [draft, setDraft] = useState<ShipTo>(saved ?? EMPTY);
  const open = () => {
    setOwn(saved !== null);
    setDraft(saved ?? EMPTY);
    onEdit(true);
  };
  const ready = !own || complete(draft.address);

  return (
    <div className="grid min-w-0 gap-3">
      <button
        aria-expanded={editing}
        aria-label={`Edit ${shipment.manufacturer} ship-to`}
        className="flex min-h-9 min-w-0 items-center gap-2 rounded-md text-left text-sm outline-none hover:text-primary focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:opacity-50"
        disabled={busy}
        onClick={() => (editing ? onEdit(false) : open())}
        type="button"
      >
        <MapPin
          aria-hidden="true"
          className="size-3.5 shrink-0 text-muted-foreground"
        />
        <span className="shrink-0 font-medium">Ships to</span>
        <span
          className={cn(
            "line-clamp-2 min-w-0 break-words",
            saved ? "font-medium text-foreground" : "text-muted-foreground",
          )}
        >
          {saved ? addressLine(saved.address) : "Quote delivery address"}
        </span>
        <Pencil
          aria-hidden="true"
          className="size-3.5 shrink-0 text-muted-foreground"
        />
      </button>

      {editing ? (
        <form
          className="grid gap-3 rounded-lg border bg-muted/20 p-3"
          onSubmit={(event) => {
            event.preventDefault();
            if (ready) onSave(own ? draft : null);
          }}
        >
          <ReviewChoiceGroup
            disabled={busy}
            label="Ships to"
            onChange={(next) => setOwn(next === "own")}
            options={[
              { value: "quote", label: "Quote delivery address" },
              { value: "own", label: "Different address" },
            ]}
            value={own ? "own" : "quote"}
          />
          {own ? (
            <>
              <div className="grid gap-3 sm:grid-cols-6">
                {ADDRESS_FIELDS.map(([key, label, span]) => (
                  <div className={span} key={key}>
                    <Field label={label}>
                      <InlineFieldInput
                        className="text-foreground"
                        onChange={(event) =>
                          setDraft((current) => ({
                            ...current,
                            address: {
                              ...current.address,
                              [key]: event.target.value,
                            },
                          }))
                        }
                        required={key !== "name" && key !== "line2"}
                        value={draft.address[key] ?? ""}
                      />
                    </Field>
                  </div>
                ))}
              </div>
              <div className="grid gap-3 sm:grid-cols-3">
                {CONTACT_FIELDS.map(([key, label, type]) => (
                  <Field key={key} label={label}>
                    <InlineFieldInput
                      className="text-foreground"
                      onChange={(event) =>
                        setDraft((current) => ({
                          ...current,
                          contact: {
                            ...EMPTY.contact,
                            ...current.contact,
                            [key]: event.target.value,
                          },
                        }))
                      }
                      placeholder="Quote contact"
                      type={type}
                      value={draft.contact?.[key] ?? ""}
                    />
                  </Field>
                ))}
              </div>
            </>
          ) : null}
          <div className="flex flex-wrap justify-end gap-2">
            <Button
              disabled={busy}
              onClick={() => onEdit(false)}
              size="sm"
              type="button"
              variant="ghost"
            >
              Cancel
            </Button>
            <LoadingButton
              disabled={!ready || busy}
              loading={pending}
              loadingText="Pricing..."
              size="sm"
              type="submit"
            >
              Save ship-to
            </LoadingButton>
          </div>
        </form>
      ) : null}
    </div>
  );
}
